import type { SupportAttachment } from "./pusher-events";

/**
 * Legacy DB / Pusher shape produced before the canonical SupportAttachment was adopted.
 */
interface LegacyAttachment {
  url: string;
  name: string;
  contentType?: string;
  size?: number;
}

/**
 * Accepts any raw value from a DB row or Pusher event and returns a canonical
 * SupportAttachment, or null when the value is too malformed to be useful.
 *
 * Handles:
 *  - Canonical: { objectPath, filename, contentType, size }
 *  - Legacy:    { url, name, contentType, size }  (objectPath = url, filename = name)
 *  - Anything missing both objectPath and url → null (rejected)
 */
export function normalizeSupportAttachment(raw: unknown): SupportAttachment | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;

  // Canonical shape
  if (typeof r.objectPath === "string" && r.objectPath) {
    return {
      id: typeof r.id === "string" ? r.id : undefined,
      objectPath: r.objectPath,
      filename: typeof r.filename === "string" && r.filename ? r.filename : "attachment",
      contentType: typeof r.contentType === "string" ? r.contentType : "application/octet-stream",
      size: typeof r.size === "number" ? r.size : 0,
    };
  }

  // Legacy shape: { url, name, … }
  if (typeof (r as unknown as LegacyAttachment).url === "string" && (r as unknown as LegacyAttachment).url) {
    const legacy = r as unknown as LegacyAttachment;
    return {
      objectPath: legacy.url,
      filename: typeof legacy.name === "string" && legacy.name ? legacy.name : "attachment",
      contentType: typeof legacy.contentType === "string" ? legacy.contentType : "application/octet-stream",
      size: typeof legacy.size === "number" ? legacy.size : 0,
    };
  }

  // Malformed — neither objectPath nor url is present / non-empty
  return null;
}

/**
 * Returns the full URL for an attachment objectPath.
 * objectPath is stored with a leading /objects/ prefix (canonical format).
 * This function is idempotent: if objectPath already starts with /objects/,
 * it is returned as-is. If it starts with objects/ (no leading slash), a /
 * is prepended. Otherwise /objects/ is prepended.
 */
export function attachmentUrl(objectPath: string): string {
  if (objectPath.startsWith("/objects/")) {
    return objectPath;
  }
  if (objectPath.startsWith("objects/")) {
    return `/${objectPath}`;
  }
  return `/objects/${objectPath}`;
}
